// src/pages/cadastros/Maquinas/MaquinasPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Can } from "../../../components/Can";

import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useEmpresa } from "../../../contexts/EmpresaContext";
import { useIsMobile } from "../../../hooks/useIsMobile";
import LayoutDesktop from "../../../layouts/LayoutDesktop";
import LayoutMobile from "../../../layouts/LayoutMobile";
import FiltroBar from "../../../components/FiltroBar/FiltroBar";
import CardListMobile from "../../../templates/mobile/CardListMobile";
import {
  IconHash,
  IconSector,
  IconSettings,
  IconFactory,
  IconKey,
} from "../../../components/Icons/Icons";
import { useSortableTable, SortableHeader } from "../../../hooks/useSortableTable";
import { pode } from "../../../utils/permissoes";

import { db } from "../../../config/firebase";
import {
  collection,
  deleteField,
  getDocs,
  query,
  where,
  deleteDoc,
  doc,
  writeBatch,
  updateDoc,
  serverTimestamp,
  limit,
} from "firebase/firestore";

function renderCodigo(e) {
  if (e.codigo) return e.codigo;
  if (e.codigoSequencial != null) {
    return `EQ${String(e.codigoSequencial).padStart(4, "0")}`;
  }
  if (e.id) return e.id.slice(0, 8);
  return "-";
}

function formatarDataBR(dataISO) {
  if (!dataISO) return "-";
  // aceita "YYYY-MM-DD" ou timestamp/Date
  try {
    if (typeof dataISO?.toDate === "function") {
      const dt = dataISO.toDate();
      if (!Number.isNaN(dt.getTime())) return dt.toLocaleDateString("pt-BR");
    }
    if (typeof dataISO === "string") {
      const dt = new Date(dataISO);
      if (!Number.isNaN(dt.getTime())) return dt.toLocaleDateString("pt-BR");
      return dataISO;
    }
    const dt = new Date(dataISO);
    if (!Number.isNaN(dt.getTime())) return dt.toLocaleDateString("pt-BR");
    return "-";
  } catch {
    return "-";
  }
}

function MaquinasPage() {
  const { tema, alterarTema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const canCriar = pode(usuarioLogado, "cadastros", "maquinas", "form");
  const canEditar = pode(usuarioLogado, "cadastros", "maquinas", "altera");
  const { modoGlobal, empresaAtual } = useEmpresa();
  const navigate = useNavigate();
  const isMobile = useIsMobile(900);
  const [searchParams] = useSearchParams();
  const planejamentoMode = searchParams.get("planejamento") === "1";

  const [equipamentos, setEquipamentos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [academias, setAcademias] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [carregandoAcademias, setCarregandoAcademias] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [sidebarAberta, setSidebarAberta] = useState(false);

  const [empresaSelecionada, setEmpresaSelecionada] = useState("");
  const [academiaSelecionada, setAcademiaSelecionada] = useState("");

  const [campoFiltro, setCampoFiltro] = useState("nome");
  const [textoFiltro, setTextoFiltro] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [checklistModelos, setChecklistModelos] = useState([]);
  const [carregandoChecklists, setCarregandoChecklists] = useState(false);
  const [checklistRascunhos, setChecklistRascunhos] = useState({});
  const [salvandoChecklistId, setSalvandoChecklistId] = useState("");

  const camposFiltro = [
    { value: "codigo", label: "Código" },
    { value: "nome", label: "Nome" },
    { value: "tipo", label: "Tipo" },
    { value: "marca", label: "Marca" },
    { value: "modelo", label: "Modelo" },
    { value: "numeroSerie", label: "Nº série" },
    { value: "cor", label: "Cor" },
    { value: "montadoPor", label: "Montado por" },
  ];

  // Carrega empresas
  useEffect(() => {
    async function carregarEmpresas() {
      if (!usuarioLogado?.grupoId) return;

      try {
        const ref = collection(db, "empresas");
        const q = query(ref, where("grupoId", "==", usuarioLogado.grupoId));
        const snap = await getDocs(q);
        const lista = snap.docs
          .map((docSnap) => ({
            id: docSnap.id,
            nome: docSnap.data().nome || docSnap.data().razaoSocial || docSnap.id,
            ...docSnap.data(),
          }))
          .sort((a, b) => String(a.nome).localeCompare(String(b.nome), "pt-BR"));
        setEmpresas(lista);
      } catch (e) {
        console.error("Erro ao carregar empresas:", e);
      }
    }

    carregarEmpresas();
  }, [usuarioLogado]);

  // Carrega academias quando empresa é selecionada
  useEffect(() => {
    async function carregarAcademias() {
      if (!usuarioLogado?.grupoId || !empresaSelecionada) {
        setAcademias([]);
        setAcademiaSelecionada("");
        return;
      }

      setCarregandoAcademias(true);
      try {
        const ref = collection(db, "setores");
        const q = query(
          ref,
          where("grupoId", "==", usuarioLogado.grupoId),
          where("empresaId", "==", empresaSelecionada)
        );
        const snap = await getDocs(q);
        const lista = snap.docs
          .map((docSnap) => ({
            id: docSnap.id,
            nome: docSnap.data().nome || docSnap.id,
            ...docSnap.data(),
          }))
          .sort((a, b) => String(a.nome).localeCompare(String(b.nome), "pt-BR"));
        setAcademias(lista);
        setAcademiaSelecionada("");
      } catch (e) {
        console.error("Erro ao carregar academias:", e);
        setAcademias([]);
      } finally {
        setCarregandoAcademias(false);
      }
    }

    carregarAcademias();
  }, [usuarioLogado, empresaSelecionada]);

  useEffect(() => {
    async function carregarChecklists() {
      if (!usuarioLogado?.grupoId) {
        setChecklistModelos([]);
        return;
      }

      setCarregandoChecklists(true);
      try {
        const ref = collection(db, "checklistsModelos");
        const q = query(ref, where("grupoId", "==", usuarioLogado.grupoId), limit(400));
        const snap = await getDocs(q);
        const lista = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => String(a.nome || a.codigo || "").localeCompare(String(b.nome || b.codigo || ""), "pt-BR"));
        setChecklistModelos(lista);
      } catch (e) {
        console.error("Erro ao carregar checklists de instalação:", e);
        setChecklistModelos([]);
      } finally {
        setCarregandoChecklists(false);
      }
    }

    carregarChecklists();
  }, [usuarioLogado?.grupoId]);

  // Carrega equipamentos
  useEffect(() => {
    async function carregarEquipamentos() {
      if (!usuarioLogado) return;

      setCarregando(true);
      setErro("");

      try {
        const ref = collection(db, "maquinas");
        const filtros = [];

        if (usuarioLogado.grupoId) {
          filtros.push(where("grupoId", "==", usuarioLogado.grupoId));
        } else {
          filtros.push(where("empresaNome", "==", "__nulo__"));
        }

        // Filtra por empresa selecionada se houver
        if (empresaSelecionada) {
          filtros.push(where("empresaId", "==", empresaSelecionada));
        }

        const q = query(ref, ...filtros);
        const snap = await getDocs(q);
        const lista = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        setEquipamentos(lista);
      } catch (e) {
        console.error("Erro ao carregar equipamentos:", e);
        setErro("Erro ao carregar equipamentos. Tente novamente.");
      } finally {
        setCarregando(false);
      }
    }

    carregarEquipamentos();
  }, [usuarioLogado, empresaSelecionada]);

  const equipamentosFiltrados = equipamentos
    .filter((e) => {
      // Filtra por academia se selecionada
      if (academiaSelecionada && e.setorId !== academiaSelecionada) {
        return false;
      }
      return true;
    })
    .filter((e) => {
      const texto = textoFiltro.trim().toLowerCase();
      if (!texto) return true;

      let valorBase = "";
      if (campoFiltro === "codigo") {
        valorBase = renderCodigo(e);
      } else {
        valorBase = e[campoFiltro] ?? "";
      }

      return String(valorBase).toLowerCase().includes(texto);
    });

  useEffect(() => {
    const next = {};
    equipamentos.forEach((e) => {
      next[e.id] = String(e?.checklistInstalacaoId || "");
    });
    setChecklistRascunhos(next);
  }, [equipamentos]);

  const { sortedData, requestSort, getSortDirection } = useSortableTable(equipamentosFiltrados, "nome");

  const checklistNomeById = new Map(
    checklistModelos.map((m) => [String(m.id), String(m.nome || m.codigo || m.id)])
  );

  async function salvarChecklistLinha(id, checklistInstalacaoId) {
    const alvo = equipamentos.find((e) => e.id === id);
    if (!alvo) return;

    const checklistId = String(checklistInstalacaoId || "").trim();
    const checklistNome = checklistId ? String(checklistNomeById.get(checklistId) || "") : "";

    setErro("");
    setSucesso("");
    setSalvandoChecklistId(id);

    try {
      await updateDoc(doc(db, "maquinas", id), {
        checklistInstalacaoId: checklistId || null,
        checklistInstalacaoNome: checklistNome,
        checklistInstalacaoAtiva: !!checklistId,
        checklistConcluido: checklistId ? false : deleteField(),
        checklistConcluidoEm: deleteField(),
        checklistConcluidoAtendimentoId: deleteField(),
        checklistUltimaExecucao: deleteField(),
        atualizadoEm: serverTimestamp(),
      });

      setEquipamentos((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                checklistInstalacaoId: checklistId || null,
                checklistInstalacaoNome: checklistNome,
                checklistInstalacaoAtiva: !!checklistId,
                checklistConcluido: checklistId ? false : undefined,
              }
            : item
        )
      );

      setSucesso("Checklist de instalação atualizado.");
    } catch (e) {
      console.error("Erro ao atualizar checklist do equipamento:", e);
      setErro("Erro ao salvar checklist deste equipamento.");
    } finally {
      setSalvandoChecklistId("");
    }
  }

  async function handleChecklistChange(id, checklistInstalacaoId) {
    setChecklistRascunhos((prev) => ({ ...prev, [id]: checklistInstalacaoId }));
    await salvarChecklistLinha(id, checklistInstalacaoId);
  }

  async function handleExcluir(equipamentoId) {
    if (!equipamentoId) return;

    const confirmar = window.confirm(
      "Tem certeza que deseja excluir este equipamento? Essa ação não pode ser desfeita."
    );
    if (!confirmar) return;

    setCarregando(true);
    setErro("");

    // 1) Tenta inativar preventivas vinculadas (best-effort). Se falhar, não bloqueia a exclusão.
    try {
      const snapPreventivas = await getDocs(
        query(collection(db, "preventivas"), where("maquinaId", "==", equipamentoId))
      );

      if (!snapPreventivas.empty) {
        const docs = snapPreventivas.docs;
        for (let i = 0; i < docs.length; i += 450) {
          const chunk = docs.slice(i, i + 450);
          const batch = writeBatch(db);
          chunk.forEach((d) => {
            batch.update(d.ref, {
              ativo: false,
              proximaData: null,
              inativadoEm: serverTimestamp(),
              inativadoPor: usuarioLogado?.uid || "",
              atualizadoEm: serverTimestamp(),
            });
          });
          await batch.commit();
        }
      }
    } catch (ePrev) {
      console.warn("Não foi possível inativar preventivas vinculadas ao equipamento:", ePrev);
    }

    // 2) Exclui o equipamento
    try {
      await deleteDoc(doc(db, "maquinas", equipamentoId));
      setEquipamentos((prev) => prev.filter((e) => e.id !== equipamentoId));
    } catch (eDel) {
      console.error("Erro ao excluir equipamento:", eDel);
      const code = eDel?.code || "";
      if (code === "permission-denied") {
        setErro(
          "Sem permissão para excluir equipamento. Verifique seu nível (Administrador/Gestor) e se as regras do Firestore foram publicadas."
        );
      } else {
        setErro("Erro ao excluir equipamento. Tente novamente.");
      }
    } finally {
      setCarregando(false);
    }
  }


  const itemsParaCards = equipamentosFiltrados.map((e) => ({
    id: e.id,
    nome: e.nome || "Sem nome",
    avatarUrl:
      e.fotoUrl ||
      (Array.isArray(e.fotosUrls) && e.fotosUrls.length > 0 ? e.fotosUrls[0] : undefined) ||
      (Array.isArray(e.fotos) && e.fotos.length > 0 ? e.fotos[0] : undefined),
    subtitulo: [e.setorNome, e.tipo, e.marca, e.modelo].filter(Boolean).join(" • ") || "-",
    detalhes: [
      { icone: <IconHash size="14px" />, label: "Código", valor: renderCodigo(e) },
      { icone: <IconSector size="14px" />, label: "Academia", valor: e.setorNome || "-" },
      { icone: <IconSettings size="14px" />, label: "Tipo", valor: e.tipo || "-" },
      { icone: <IconFactory size="14px" />, label: "Marca", valor: e.marca || "-" },
      { icone: <IconSettings size="14px" />, label: "Modelo", valor: e.modelo || "-" },
      { icone: <IconSector size="14px" />, label: "Cor", valor: e.cor || "-" },
      { icone: <IconKey size="14px" />, label: "Condição", valor: e.condicoesIniciais ? String(e.condicoesIniciais) : "-" },
    ],
  }));

  if (isMobile) {
    return (
      <LayoutMobile
        tema={tema}
        alterarTema={alterarTema}
        titulo="Equipamentos"
        sidebarAberta={sidebarAberta}
        onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
        abaBottom="cadastros"
      >
        <CardListMobile
          items={itemsParaCards}
          carregando={carregando}
          erro={erro}
          carregandoMessage="Carregando equipamentos..."
          titulo="Equipamentos"
          searchPlaceholder="Buscar equipamento..."
          novoLabel="Novo Equipamento"
          emptyMessage="Nenhum equipamento encontrado"
          onHome={() => navigate("/")}
          onNovo={canCriar && !planejamentoMode ? () => navigate("/cadastros/equipamentos/novo") : undefined}
          onEditar={canEditar ? (id) => navigate(`/cadastros/equipamentos/editar/${id}`) : undefined}
          getSearchText={(item) => `${item.nome} ${item.subtitulo}`}
        />
      </LayoutMobile>
    );
  }

  return (
    <LayoutDesktop tema={tema} titulo="Equipamentos" subtitulo="">
      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>Equipamentos cadastrados</h3>
            <p>Lista de equipamentos vinculados ao grupo atual.</p>
          </div>

          <div className="pc-table-header-actions">
            {canCriar && !planejamentoMode && (
              <Can modulo="cadastros" recurso="maquinas" acao="form">
                <button
                  type="button"
                  className="pc-btn-primario"
                  onClick={() => navigate("/cadastros/equipamentos/novo")}
                >
                  + Novo Equipamento
                </button>
              </Can>
            )}
          </div>
        </header>

        {/* Selecionar Cliente e Academia */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid rgba(148,163,184,0.2)", display: "flex", gap: "16px", alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ flex: "0 1 280px" }}>
            <label style={{ display: "block", fontSize: "13px", marginBottom: "6px", opacity: 0.85 }}>Cliente</label>
            <select
              value={empresaSelecionada}
              onChange={(e) => setEmpresaSelecionada(e.target.value)}
              className="pc-input"
              style={{ width: "100%" }}
            >
              <option value="">Selecione o cliente</option>
              {empresas.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nome}
                </option>
              ))}
            </select>
          </div>

          <div style={{ flex: "0 1 280px" }}>
            <label style={{ display: "block", fontSize: "13px", marginBottom: "6px", opacity: 0.85 }}>Academia</label>
            <select
              value={academiaSelecionada}
              onChange={(e) => setAcademiaSelecionada(e.target.value)}
              className="pc-input"
              style={{ width: "100%", opacity: empresaSelecionada ? 1 : 0.5 }}
              disabled={!empresaSelecionada || carregandoAcademias}
            >
              <option value="">Todas as academias</option>
              {academias.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.nome}
                </option>
              ))}
            </select>
          </div>
        </div>

        <FiltroBar
          campos={camposFiltro}
          campoSelecionado={campoFiltro}
          onCampoChange={setCampoFiltro}
          textoFiltro={textoFiltro}
          onTextoChange={setTextoFiltro}
          onAdicionarClick={!planejamentoMode ? () => navigate("/cadastros/equipamentos/novo") : undefined}
          placeholder="Filtrar equipamentos..."
          mostrarAdicionar={false}
        />

        <div className="pc-table-wrapper">
          {erro && (
            <div
              style={{
                background: "#7f1d1d",
                color: "#fecaca",
                padding: 8,
                borderRadius: 8,
                marginBottom: 12,
                fontSize: 14,
              }}
            >
              {erro}
            </div>
          )}

          {sucesso && (
            <div
              style={{
                background: "#14532d",
                color: "#bbf7d0",
                padding: 8,
                borderRadius: 8,
                marginBottom: 12,
                fontSize: 14,
              }}
            >
              {sucesso}
            </div>
          )}

          {carregando ? (
            <p style={{ padding: 10 }}>Carregando equipamentos...</p>
          ) : sortedData.length === 0 ? (
            <p style={{ padding: 10 }}>Nenhum equipamento encontrado.</p>
          ) : (
            <table className="pc-table pc-table-expandable">
              <thead>
                <tr>
                  <th style={{ width: 44 }} />
                  <SortableHeader
                    label="Código"
                    sortKey="codigoSequencial"
                    onSort={requestSort}
                    sortDirection={getSortDirection("codigoSequencial")}
                  />
                  <SortableHeader
                    label="Nome"
                    sortKey="nome"
                    onSort={requestSort}
                    sortDirection={getSortDirection("nome")}
                  />
                  <SortableHeader
                    label="Academia"
                    sortKey="setorNome"
                    onSort={requestSort}
                    sortDirection={getSortDirection("setorNome")}
                  />
                  <SortableHeader
                    label="Tipo"
                    sortKey="tipo"
                    onSort={requestSort}
                    sortDirection={getSortDirection("tipo")}
                  />
                  <SortableHeader
                    label="Marca"
                    sortKey="marca"
                    onSort={requestSort}
                    sortDirection={getSortDirection("marca")}
                  />
                  <SortableHeader
                    label="Modelo"
                    sortKey="modelo"
                    onSort={requestSort}
                    sortDirection={getSortDirection("modelo")}
                  />
                  <th>Checklist instalação</th>
                  <th style={{ textAlign: "right" }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((e) => {
                  const aberto = expandedId === e.id;

                  return (
                    <React.Fragment key={e.id}>
                      <tr className={aberto ? "pc-row-open" : undefined}>
                        <td>
                          <button
                            type="button"
                            className="pc-expand-btn"
                            aria-label={aberto ? "Recolher" : "Expandir"}
                            onClick={() => setExpandedId((prev) => (prev === e.id ? null : e.id))}
                          >
                            <span className={aberto ? "pc-expand-chevron open" : "pc-expand-chevron"}>
                              ▸
                            </span>
                          </button>
                        </td>

                        <td>{renderCodigo(e)}</td>
                        <td style={{ fontWeight: 600 }}>{e.nome || "-"}</td>
                        <td>{e.setorNome || "-"}</td>
                        <td>{e.tipo || "-"}</td>
                        <td>{e.marca || "-"}</td>
                        <td>{e.modelo || "-"}</td>
                        <td>
                          {canEditar ? (
                            <select
                              className="pc-input"
                              value={checklistRascunhos[e.id] ?? String(e?.checklistInstalacaoId || "")}
                              onChange={(ev) => handleChecklistChange(e.id, String(ev.target.value || ""))}
                              disabled={carregandoChecklists || salvandoChecklistId === e.id}
                              style={{ minWidth: 160 }}
                            >
                              <option value="">Sem checklist</option>
                              {checklistModelos.map((m) => (
                                <option key={m.id} value={m.id}>
                                  {m.nome || m.codigo || m.id}
                                </option>
                              ))}
                            </select>
                          ) : (
                            e.checklistInstalacaoNome || "-"
                          )}
                        </td>
                        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                          <button
                            type="button"
                            className="pc-btn-primario"
                            style={{ fontSize: 13, padding: "4px 10px", marginRight: 8 }}
                            title="Abrir OS para este equipamento"
                            onClick={() => navigate("/ordens-servico/abrir-nova-os", { state: { equipamentoId: e.id, equipamento: e } })}
                          >
                            Abrir OS
                          </button>
                          <Can modulo="cadastros" recurso="maquinas" acao="altera">
                            <button
                              type="button"
                              className="pc-icon-btn"
                              title="Editar equipamento"
                              onClick={() => navigate(`/cadastros/equipamentos/editar/${e.id}`)}
                            >
                              ✏️
                            </button>
                          </Can>
                          <Can modulo="cadastros" recurso="maquinas" acao="altera">
                            <button
                              type="button"
                              className="pc-icon-btn"
                              title="Apagar equipamento"
                              onClick={() => handleExcluir(e.id)}
                            >
                              🗑️
                            </button>
                          </Can>
                        </td>
                      </tr>

                      {aberto && (
                        <tr className="pc-expanded-row">
                          <td colSpan={9}>
                            <div className="pc-expand-panel">
                              <div className="pc-expand-grid">
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Montado por</div>
                                  <div className="pc-expand-value">{e.montadoPor || "-"}</div>
                                </div>

                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Instalado</div>
                                  <div className="pc-expand-value">{formatarDataBR(e.instaladoEm)}</div>
                                </div>

                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Código</div>
                                  <div className="pc-expand-value">{renderCodigo(e)}</div>
                                </div>

                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Academia</div>
                                  <div className="pc-expand-value">{e.setorNome || "-"}</div>
                                </div>

                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Modelo</div>
                                  <div className="pc-expand-value">{e.modelo || "-"}</div>
                                </div>

                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Nº série / Rastreabilidade</div>
                                  <div className="pc-expand-value">{e.numeroSerie || "-"}</div>
                                </div>

                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Cor</div>
                                  <div className="pc-expand-value">{e.cor || "-"}</div>
                                </div>

                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Condição</div>
                                  <div className="pc-expand-value">
                                    {e.condicoesIniciais ? String(e.condicoesIniciais) : "-"}
                                  </div>
                                </div>

                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">ID</div>
                                  <div className="pc-expand-value">{e.id}</div>
                                </div>
                              </div>

                              {e.descricao && (
                                <div style={{ marginTop: 12 }}>
                                  <div className="pc-expand-label" style={{ marginBottom: 6 }}>
                                    Descrição
                                  </div>
                                  <div className="pc-expand-value" style={{ whiteSpace: "pre-wrap" }}>
                                    {e.descricao}
                                  </div>
                                </div>
                              )}

                              {e.fotoUrl && (
                                <div style={{ marginTop: 12 }}>
                                  <div className="pc-expand-label" style={{ marginBottom: 6 }}>
                                    Foto
                                  </div>
                                  <img
                                    src={e.fotoUrl}
                                    alt="Foto do equipamento"
                                    style={{
                                      maxWidth: 260,
                                      width: "100%",
                                      borderRadius: 12,
                                      border: "1px solid rgba(255,255,255,0.08)",
                                    }}
                                  />
                                </div>
                              )}

                              <div className="pc-expand-actions">
                                <button
                                  type="button"
                                  className="pc-btn-primario"
                                  onClick={() => navigate("/ordens-servico/abrir-nova-os", { state: { equipamentoId: e.id, equipamento: e } })}
                                >
                                  Abrir OS
                                </button>

                                <Can modulo="cadastros" recurso="maquinas" acao="altera">
                                  <button
                                    type="button"
                                    className="pc-btn-secundario"
                                    onClick={() => navigate(`/cadastros/equipamentos/editar/${e.id}`)}
                                  >
                                    Editar
                                  </button>
                                </Can>

                                <Can modulo="cadastros" recurso="maquinas" acao="deleta">
                                  <button
                                    type="button"
                                    className="pc-btn-perigo"
                                    onClick={() => handleExcluir(e.id)}
                                  >
                                    Excluir
                                  </button>
                                </Can>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </LayoutDesktop>
  );
}

export default MaquinasPage;
